// Daily-view types — mirrors what the PJ Daily Solution writes to
// run.result_tables.master_production_plan (one row per filled / unfilled flow).
//
// Field names match the CSV column names verbatim (incl. the space in 'rm type'
// and 'available matl') so we can read raw rows without a translation layer.

export type ModelType = 'no_pos' | '5_day' | 'full' | 'solon_only' | string;

export interface MPPRow {
  model_type: ModelType;
  filled_from: string | null;
  day: number | null;
  date?: string | null; // calendar date for the scheduled day (2026-06-09 model)
  order: string | null; // "Customer Name (AU#####)"
  sku: string | null;
  bulk: string | null;
  container: string | null;
  'rm type': string | null;
  raws: string | null;
  'available matl': string | null;
  po_type: string | null;
  critical_po: 'Yes' | 'No' | 'N/A' | null;
  po_vendor: string | null;
  po_number: string | null;
  po_landing_date: string | null;
  po_confirmed: string | null;
  order_lbs: number | null;
  s_to_o: number | null;
  // Container units allocated to the order. Older daily models name this
  // `units_to_fill`; "PJ Daily Solution 2026-06-09" renamed it to `c_to_o`.
  // Read via rowUnits() which coalesces the two.
  units_to_fill?: number | null;
  c_to_o?: number | null;
  // PO quantity. Older models emit a number; the 2026-06-09 model emits a
  // comma-joined string ("100, 200") or "-". Read via poQtyNum().
  po_qty: number | string | null;
  // Solon warehouse inventory. Older models: `solon inventory today`;
  // 2026-06-09: `inv_solon`.
  'solon inventory today'?: number | null;
  inv_solon?: number | null;
  sankey_value: number | null;
}

// Selection state surfaced to the dashboard so it can drive the detail panel.
export type SelectedTarget =
  | { kind: 'node'; nodeId: string }
  | { kind: 'link'; linkKey: string };

export type DayFilterValue = 'all' | '1' | '2' | '3' | '4' | '5';

export interface ChipStats {
  filledOrders: number;
  unfilledOrders: number;
  lbsShipped: number;
  lbsRequested: number;
  criticalOrders: number;
  criticalFlowRows: number;
  fillRate: number; // 0..1
}

// --- Daily-View priority + fill-ticket-coverage filter state --------------

// Three distinct, non-overlapping coverage views (an earlier "hide covered" vs
// "only missing" pair was redundant — both showed the uncovered set):
//   'all'     — every line
//   'covered' — only lines already covered by an open fill ticket
//   'missing' — only lines with NO open ticket (the fill tickets to create)
export type CoverageMode = 'all' | 'covered' | 'missing';

export interface DailyViewFilterState {
  coverage: CoverageMode;
  topN: number; // keep only the top-N orders by size (lbs), critical first (0 = all)
  criticalOnly: boolean; // restrict to orders with a critical PO
}

export const EMPTY_DAILY_FILTERS: DailyViewFilterState = {
  coverage: 'all',
  topN: 0,
  criticalOnly: false,
};
